import { clientUrlPath } from "common/utilities/browser/url/url";
import { generateRandomString } from "common/utilities/core/string";
import * as I from "core/api/coreApiClient";
import type { ReplacedCommands } from "core/redux/reducers/page/pageReducer.types";
import type { EnhancedCommand } from "core/utilities/core.types";
import I18n from "i18n/en-US";

export const COMMAND_CANCEL_ID = "cancelAction";

export function isTableSelectionCommand(c: I.Command): boolean {
  return (
    c.entitySelection === I.EntitySelectionConstants.Optional ||
    c.entitySelection === I.EntitySelectionConstants.Required ||
    (!c.entitySelection && c.commands?.length !== 0 && c.commands.every((subc) => isTableSelectionCommand(subc)))
  );
}

export function onlyTableSelectionCommands(commands: I.Command[]) {
  return commands.filter((c) => isTableSelectionCommand(c) || c.fetchUrl);
}

export function excludeTableSelectionCommands(commands: I.Command[]) {
  return commands.filter((c) => !isTableSelectionCommand(c) || c.fetchUrl);
}

export function excludeExportCommand(commands: I.Command[]) {
  return commands.filter((c) => c.styleHint !== "export");
}

// Table related commands might require a selection
// we disable the command here if the condition is not fullfiled
export function commandsWithSelectionState(commands: I.Command[], selectionCount: number, isProcessing: boolean) {
  return commands.map((command) => {
    const updatedCommand: I.Command = {
      ...command,
      commands: commandsWithSelectionState(command.commands, selectionCount, isProcessing),
    };

    if (command.entitySelection === I.EntitySelectionConstants.Required) {
      if (isProcessing) {
        updatedCommand.active = false;
        updatedCommand.title = I18n.utils.commandsWithSelectionState.saving;
      } else if (
        command.active &&
        command.numberOfEntitiesRequired !== undefined &&
        command.numberOfEntitiesRequired > selectionCount
      ) {
        updatedCommand.active = false;
        updatedCommand.title = I18n.utils.commandsWithSelectionState.requiresNumber(
          command.numberOfEntitiesRequired,
          command.title
        );
      } else if (command.active && selectionCount === 0) {
        updatedCommand.active = false;
        updatedCommand.title = I18n.utils.commandsWithSelectionState.requires(command.title);
      }
    }

    return updatedCommand;
  });
}

export function formatCommandUrl(jsfUrl: string, { url, commandType }: I.Command) {
  if (commandType === I.CommandTypeConstants.JSFHREF) {
    return `${jsfUrl}${url as string}`;
  }

  if (commandType === I.CommandTypeConstants.FileDownload) {
    const fileUrl = `${jsfUrl}${url as string}`;
    return clientUrlPath(`download-file?url=${encodeURIComponent(fileUrl)}`);
  }

  return url;
}

// Any URL/path change that is handled by the current react app router
export function isCommandForReactRouter({ commandType }: I.Command) {
  return !(
    commandType === I.CommandTypeConstants.JSFHREF ||
    commandType === I.CommandTypeConstants.ExternalHREF ||
    commandType === I.CommandTypeConstants.FileDownload
  );
}

export function smalifyCommands(commands: I.Command[]) {
  return commands.map((command) => {
    const enhancedCommand: EnhancedCommand = { ...command, small: true };
    return enhancedCommand;
  });
}

/**
 * Turn any command into a command that requires confirmation
 *
 * @param command that requires confirmation
 * @param dispatchShowConfirmation redux action dispatcher
 */
export const commandToConfirmCommand = (
  command: I.Command,
  dispatchShowConfirmation: (confirmation: I.Confirm) => void,
  confirmLabel?: string
) => {
  const confirmationCommand: EnhancedCommand = {
    ...command,
    id: `confirm-${command.id}`,
    active: true,
    httpVerb: undefined,
    url: undefined,
    commandType: undefined,
    commands: [],
    onClick: () => {
      const cancelCommand: EnhancedCommand = {
        id: "cancelAction",
        label: I18n.utils.commandToConfirmCommand.cancel,
        title: I18n.utils.commandToConfirmCommand.cancelTitle,
        active: true,
        commands: [],
      };

      const confirm: I.Confirm = {
        label: command.title,
        title: "",
        confirmCommand: { ...command, label: confirmLabel || I18n.utils.commandToConfirmCommand.confirm },
        cancelCommand,
      };

      dispatchShowConfirmation(confirm);
    },
  };

  return confirmationCommand;
};

/**
 * Some commands might fetch other commands asynchronously. Those new commands are stored in replacedCommands.
 * The final commands, including the replacedCommands are consolidated below.
 */
export const replaceCommands = (commands: EnhancedCommand[], replacedCommands?: ReplacedCommands) => {
  if (!replacedCommands) return commands;

  return commands.reduce<EnhancedCommand[]>((acc, command) => {
    let newAcc: EnhancedCommand[] = [...acc];
    if (command.fetchUrl && replacedCommands[command.id]) {
      // check for fetchUrl just as safeguard
      newAcc = newAcc.concat(replacedCommands[command.id]);
    } else {
      newAcc.push(command);
    }
    return newAcc;
  }, []);
};

/**
 * Create a new command that will contain all given commands.
 * It will be rendered as a dropdown.
 */
export const groupCommands = (title: string, iconEl: JSX.Element, commands: EnhancedCommand[]): EnhancedCommand => ({
  id: `groupCommands-${title}-${generateRandomString()}`,
  title,
  active: true,
  commands,
  iconEl,
  inheritColor: true,
  hideArrowIcon: true,
});

export const separatorCommand = (): EnhancedCommand => ({
  id: `commandSeparator-${generateRandomString()}`,
  commandType: I.CommandTypeConstants.Separator,
  active: false,
  label: "",
  title: "",
  commands: [],
});

export const horizontalSeparatorCommand = (): EnhancedCommand => ({
  id: `commandSeparator-${generateRandomString()}`,
  commandType: I.CommandTypeConstants.Separator,
  active: false,
  label: "|",
  title: "|",
  commands: [],
});
